import { isValid } from 'date-fns';
import findLast from 'lodash/findLast';
import has from 'lodash/has';
import { isNil, isNilOrEmptyString, isTypeTimestamp } from './utils.esm.js';

const END = '(end)';
const NUM = ['=', '!=', '<>', '>', '<', '>=', '<='];
const DEFAULT = ['=', '!=', '<>'];
const STRING_TYPES = new Set(['string', 'enum', 'ip']);
const INT_TYPES = new Set(['int', 'long']);
const TYPE_OP_MAPPING = {
    int: NUM,
    long: NUM,
    timestamp: NUM,
    timestamp_high_res: NUM,
    string: ['=', '!=', '<>', 'LIKE'],
    panPrefixString: ['=', '!=', '<>'], // "in", "notin"
    enum: DEFAULT,
    '': DEFAULT
};
function isStringTypes(type) {
    return STRING_TYPES.has(type);
}
function isNumberTypes(type) {
    return INT_TYPES.has(type);
}
function isNum(c) {
    return c >= '0' && c <= '9';
}
function tokenize(text) {
    let c; // The current character.
    let from; // The index of the start of the token.
    let i = 0; // The index of the current character.
    let q; // The quote character.
    let str; // The string value.
    const result = []; // An array to hold the results.
    // Begin tokenization. If the source string is empty, return nothing.
    // Loop through text, one character at a time.
    c = text.charAt(i);
    while (!isNilOrEmptyString(c)) {
        from = i;
        // Ignore whitespace.
        if (c <= ' ') {
            i += 1;
            c = text.charAt(i);
            // name.
        }
        else if ((c >= '0' && c <= '9') ||
            (c >= 'a' && c <= 'z') ||
            (c >= 'A' && c <= 'Z') ||
            (c === '-' && isNum(text.charAt(i + 1)))) {
            str = c;
            i += 1;
            for (;;) {
                c = text.charAt(i);
                if ((c >= 'a' && c <= 'z') ||
                    (c >= 'A' && c <= 'Z') ||
                    (c >= '0' && c <= '9') ||
                    c === '_' ||
                    c === '-' ||
                    c === '.' ||
                    c === '~' ||
                    c === '`' ||
                    c === '!' ||
                    c === '@' ||
                    c === '#' ||
                    c === '$' ||
                    c === '%' ||
                    c === '^' ||
                    c === '&' ||
                    c === '*' ||
                    c === '=' ||
                    c === '+' ||
                    c === '{' ||
                    c === '}' ||
                    c === '[' ||
                    c === ']' ||
                    c === '|' ||
                    c === ';' ||
                    c === ':' ||
                    c === '<' ||
                    c === '>' ||
                    c === ',' ||
                    c === '?' ||
                    c === '/' ||
                    c === "'" ||
                    c === '"') {
                    // allow ' and " directly inside string
                    str += c;
                    i += 1;
                }
                else {
                    break;
                }
            }
            result.push(make('string', str, from, i));
        }
        else if (c === "'" || c === '"' || c === '`') {
            // string
            str = '';
            q = c;
            i += 1;
            for (;;) {
                c = text.charAt(i);
                // allow Control character in string, some code removed
                if (c === '') {
                    // in case i >= length
                    throw makeErr('unterminated_string', 'string', str, from, i, q);
                }
                // Look for the closing quote.
                if (c === q) {
                    // only support escaped quotes
                    if (text.charAt(i + 1) === q) {
                        c = q;
                        i += 1;
                    }
                    else {
                        break;
                    }
                }
                str += c;
                i += 1;
            }
            i += 1;
            result.push({ ...make('string', str, from, i), quoted: q });
            c = text.charAt(i);
        }
        else {
            // Single-character operator, add support for == != <> >= <=
            i += 1;
            let op = c;
            c = text.charAt(i);
            if ((op === '=' || op === '!' || op === '>' || op === '<') && c === '=') {
                i += 1;
                op += c;
                c = text.charAt(i);
            }
            else if (op === '<' && c === '>') {
                // <>
                i += 1;
                op += c;
                c = text.charAt(i);
            }
            result.push(make('operator', op, from, i));
        }
    }
    return result;
}
// Make a token object.
function make(type, value, from, to) {
    return { type, value, from, to };
}
function makeErr(msg, type, value, from, to, quoted) {
    const bad = { ...make(type, value, from, to), quoted };
    const result = [];
    result.push(bad);
    throw Object.assign(new SyntaxError(msg), bad, { tokens: result });
}
function validate(fieldMap, tokens, errors, supportNot) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    for (const token of tokens) {
        switch (token.kind) {
            case 'key':
                if (has(fieldMap, token.value)) {
                    const field = fieldMap[token.value];
                    let operators;
                    if (!isNil(field.operators)) {
                        operators = field.operators;
                    }
                    else {
                        if (!isNil(field.type) && !isNil(TYPE_OP_MAPPING[field.type])) {
                            operators = TYPE_OP_MAPPING[field.type];
                        }
                        else {
                            operators = TYPE_OP_MAPPING[''];
                        }
                    }
                    token.field = {
                        ...field,
                        operators: operators
                    };
                    if (token.exp && !isNil(token.exp)) {
                        token.exp.field = token.field;
                    }
                }
                else {
                    errors.push(new Error('unsupported_field_name'));
                }
                break;
            case 'op':
                if (!isNil((_a = token.exp) === null || _a === void 0 ? void 0 : _a.field)) {
                    token.field = (_b = token.exp) === null || _b === void 0 ? void 0 : _b.field;
                    if (!isNil((_c = token.field) === null || _c === void 0 ? void 0 : _c.operators) && !((_e = (_d = token.field) === null || _d === void 0 ? void 0 : _d.operators) === null || _e === void 0 ? void 0 : _e.includes(token.value.toUpperCase()))) {
                        errors.push(new Error('unsupported_operator'));
                    }
                }
                break;
            case 'value':
                if (!isNil((_f = token.exp) === null || _f === void 0 ? void 0 : _f.field)) {
                    token.field = (_g = token.exp) === null || _g === void 0 ? void 0 : _g.field;
                    if (!isNil(token.field) && !isNil((_h = token.field) === null || _h === void 0 ? void 0 : _h.type)) {
                        if (!isNil((_j = token.field) === null || _j === void 0 ? void 0 : _j.enum)) {
                            if (token.quoted !== "'" && (Number.isNaN(+token.value) || !isNil(token.quoted))) {
                                errors.push(new TypeError('enum_not_quoted'));
                            }
                        }
                        else if (isTypeTimestamp((_k = token.field) === null || _k === void 0 ? void 0 : _k.type)) {
                            const value = token.value.trim();
                            if (isNilOrEmptyString(value) || !isValid(new Date(value))) {
                                errors.push(new TypeError('bad_timestamp'));
                            }
                        }
                        else if (((_l = token.field) === null || _l === void 0 ? void 0 : _l.type) === 'boolean') {
                            const value = token.value.trim().toLowerCase();
                            if (value !== 'true' && value !== 'false') {
                                errors.push(new TypeError('bad_boolean_value'));
                            }
                            else if (!isNil(token.quoted)) {
                                errors.push(new TypeError('quoted_value'));
                            }
                        }
                        else if (STRING_TYPES.has((_m = token.field) === null || _m === void 0 ? void 0 : _m.type)) {
                            if (isNilOrEmptyString(token.quoted) && !/['"`]$/.test(token.value)) {
                                errors.push(new TypeError('string_value_not_quoted'));
                            }
                        }
                        else if (INT_TYPES.has((_o = token.field) === null || _o === void 0 ? void 0 : _o.type)) {
                            if (!isNil(token.quoted)) {
                                errors.push(new TypeError('quoted_token_value'));
                            }
                        }
                    }
                }
                break;
            case 'not':
                if (!supportNot) {
                    errors.push(new Error('NOT_not_supported'));
                }
                else if (tokens.length < 3) {
                    errors.push(new Error('incomplete_expression'));
                }
                break;
            case 'andor':
                break;
            default:
                errors.push(new Error('unrecognized_expression_part'));
        }
    }
    return !errors.length;
}
function peekToken(tokenIndex, tokens) {
    if (tokenIndex >= tokens.length) {
        return { value: '(end)', id: '(end)', type: '(end)' };
    }
    // todo: potential bug on sparse array
    return tokens[tokenIndex];
}
function assertToken(condition, msg, warn, errors) {
    if (!condition) {
        if (!warn) {
            throw new SyntaxError(msg);
        }
        errors.push(new SyntaxError(msg));
    }
}
function quotes(str) {
    return `'${str}'`;
}
// a op b
function simpleOp(tokenIndex, tokens) {
    const newTokens = [];
    for (let i = 0; i < 3; i++) {
        newTokens.push(peekToken(tokenIndex, tokens));
        tokenIndex += 1;
    }
    const o = {
        args: [newTokens[0].value, newTokens[2].value],
        op: newTokens[1].value
    };
    // added
    const kinds = ['key', 'op', 'value'];
    for (let i = 0; i < newTokens.length; i++) {
        const t = newTokens[i];
        t.kind = kinds[i];
        t.exp = o; // cross ref
    }
    return [o, tokenIndex];
}
function parenthesizedExpression(tokenIndex, tokens, errors) {
    const e = expression(tokenIndex + 1, tokens, errors, undefined, tokenIndex);
    const close = peekToken(e[1], tokens);
    tokenIndex = e[1] + 1;
    assertToken(close.value === ')', 'expecting_close_parenthesis', true, errors);
    return [e[0], tokenIndex];
}
// expr and expr
function compoundOp(tokenIndex, tokens, errors, left, leftParenthesisIndex) {
    const t = peekToken(tokenIndex, tokens);
    tokenIndex += 1;
    const o = {
        op: t.value.toUpperCase(),
        args: []
    };
    const rightExp = expression(tokenIndex, tokens, errors, undefined, leftParenthesisIndex);
    const right = rightExp[0];
    tokenIndex = rightExp[1];
    if (isNil(left)) {
        // added support not
        t.kind = 'not'; // or in
        t.exp = right;
        o.args = [];
        return [o, tokenIndex];
    }
    t.kind = 'andor';
    assertToken(!isNil(right), 'expecting_right_missing', true, errors);
    if (!isNil(right) && right.op === o.op) {
        // combine multiple and or
        right.args.unshift(left);
        return [right, tokenIndex];
    }
    o.args = [left, right];
    return [o, tokenIndex];
}
function expression(tokenIndex, tokens, errors, left, leftParenthesisIndex = -1) {
    const t = peekToken(tokenIndex, tokens);
    const upper = t.value.toUpperCase();
    let temp;
    if (t.type === '(end)') {
        return [left, tokenIndex];
    }
    switch (upper) {
        case '(':
            t.kind = 'paren';
            temp = parenthesizedExpression(tokenIndex, tokens, errors);
            return expression(temp[1], tokens, errors, temp[0], leftParenthesisIndex);
        case ')':
            t.kind = 'paren';
            assertToken(leftParenthesisIndex > -1, 'expecting_left_parenthesis', true, errors);
            assertToken(tokenIndex > leftParenthesisIndex + 1, 'empty_parenthesis', true, errors);
            return [left, tokenIndex];
        case 'NOT':
        case '!':
            temp = compoundOp(tokenIndex, tokens, errors, undefined, leftParenthesisIndex);
            return expression(temp[1], tokens, errors, temp[0], leftParenthesisIndex);
        case 'AND':
        case 'OR':
        case '&&':
        case '||':
            assertToken(!isNil(left), 'expression_left_missing', false, errors);
            temp = compoundOp(tokenIndex, tokens, errors, left, leftParenthesisIndex);
            return expression(temp[1], tokens, errors, temp[0], leftParenthesisIndex);
        default:
            assertToken(isNil(left), 'expression_to_left', false, errors);
            temp = simpleOp(tokenIndex, tokens);
            return expression(temp[1], tokens, errors, temp[0], leftParenthesisIndex);
    }
}
function parseQuery(tokens, errors) {
    const tree = expression(0, tokens, errors, undefined)[0];
    return { tree, tokens, errors };
}
function parse(exp, fields, options, commonFilters) {
    let tokens = [];
    const errors = [];
    const warnings = [];
    const parsingResult = {
        query: '',
        tree: null,
        tokens,
        errors,
        warnings,
        fieldMap: fields
    };
    if (isNil(exp) || exp.trim() === '') {
        return parsingResult;
    }
    if (!options.supportParen && /[()]/.test(exp)) {
        errors.push(new Error('parentheses_not_supported'));
        return parsingResult;
    }
    parsingResult.query = exp;
    try {
        tokens = tokenize(exp);
    }
    catch (e) {
        errors.push(e);
        if (!isNil(e.tokens)) {
            tokens = e.tokens;
        }
    }
    parsingResult.tokens = tokens;
    try {
        const { tree } = parseQuery(tokens, errors);
        if (isNil(tree)) {
            errors.push(new Error('incomplete_expression'));
            errors.push(...errors);
            return parsingResult;
        }
        if (tree.op === END ||
            tree.args.some((a) => a === END) ||
            !isNil(findLast(tokens, (t) => { var _a, _b; return !isNil(t.exp) && (((_a = t.exp) === null || _a === void 0 ? void 0 : _a.op) === END || ((_b = t.exp) === null || _b === void 0 ? void 0 : _b.args.some((a) => a === END))); }))) {
            errors.push(new SyntaxError('expression_is_incomplete'));
        }
        parsingResult.tree = tree;
    }
    catch (err) {
        errors.push(err);
    }
    validate(fields, tokens, errors, options.supportNot);
    if (errors.length === 0 && !isNil(commonFilters) && commonFilters && commonFilters.length > 0) {
        for (const token of tokens) {
            if (token.kind === 'key' && !(commonFilters === null || commonFilters === void 0 ? void 0 : commonFilters.includes(token.value))) {
                warnings.push('long_operation_warning');
            }
        }
    }
    return parsingResult;
}

export { END, STRING_TYPES, TYPE_OP_MAPPING, isNumberTypes, isStringTypes, parse, quotes };
//# sourceMappingURL=queryExpression.esm.js.map
